import React, { useState, useCallback, useEffect } from 'react';
import { Account, AccountType, CreateAccountDTO, UpdateAccountDTO } from '../../types/budget';
import { useBudget } from './BudgetContext';
import { Button } from '../ui/Button';
import styles from './AccountManager.module.css';

interface AccountManagerProps {
  onClose: () => void;
  onAccountsChanged?: () => void;
}

const PRESET_COLORS = [
  '#4A90D9', '#7B68EE', '#FF6B6B', '#4ECDC4', '#45B7D1',
  '#96CEB4', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE',
  '#85C1E9', '#F8B500', '#48C9B0', '#AF7AC5', '#5DADE2',
  '#F1948A', '#000000', '#E74C3C', '#8B949E', '#3FB950',
];

const ACCOUNT_TYPES: { value: AccountType; label: string }[] = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'investment', label: 'Investment' },
  { value: 'other', label: 'Other' },
];

const getAccountTypeLabel = (type: AccountType): string => {
  const found = ACCOUNT_TYPES.find(t => t.value === type);
  return found ? found.label : type;
};

export const AccountManager: React.FC<AccountManagerProps> = ({
  onClose,
  onAccountsChanged,
}) => {
  const {
    accounts,
    transactions,
    createAccount,
    updateAccount,
    deactivateAccount,
    reactivateAccount,
    deleteAccount,
  } = useBudget();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState<AccountType>('checking');
  const [editInstitution, setEditInstitution] = useState('');
  const [editLastFour, setEditLastFour] = useState('');
  const [editColor, setEditColor] = useState('#4A90D9');
  const [editInvertAmounts, setEditInvertAmounts] = useState(false);
  
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<AccountType>('checking');
  const [newInstitution, setNewInstitution] = useState('');
  const [newLastFour, setNewLastFour] = useState('');
  const [newColor, setNewColor] = useState('#4A90D9');
  const [newInvertAmounts, setNewInvertAmounts] = useState(false);
  
  const [isProcessing, setIsProcessing] = useState(false);

  const getTransactionCount = useCallback((accountId: string) => {
    return transactions.filter(t => t.accountId === accountId).length;
  }, [transactions]);

  const activeAccounts = accounts.filter(a => a.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  const inactiveAccounts = accounts.filter(a => !a.isActive).sort((a, b) => a.name.localeCompare(b.name));

  const handleEditStart = (account: Account) => {
    setEditingId(account.id);
    setEditName(account.name);
    setEditType(account.type);
    setEditInstitution(account.institution || '');
    setEditLastFour(account.lastFour || '');
    setEditColor(account.color);
    setEditInvertAmounts(account.invertAmounts);
  };

  const handleEditSave = async () => {
    if (!editingId || !editName.trim()) return;

    setIsProcessing(true);
    try {
      const dto: UpdateAccountDTO = {
        name: editName.trim(),
        type: editType,
        institution: editInstitution.trim() || undefined,
        lastFour: editLastFour.trim() || null,
        color: editColor,
        invertAmounts: editInvertAmounts,
      };
      await updateAccount(editingId, dto);
      setEditingId(null);
      onAccountsChanged?.();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditName('');
    setEditType('checking');
    setEditInstitution('');
    setEditLastFour('');
    setEditColor('#4A90D9');
    setEditInvertAmounts(false);
  };

  const handleDeactivate = async (id: string, name: string) => {
    const count = getTransactionCount(id);
    const message = count > 0
      ? `"${name}" has ${count} transaction${count !== 1 ? 's' : ''}. The account will be deactivated but transactions will be preserved. Continue?`
      : `Deactivate account "${name}"?`;

    if (window.confirm(message)) {
      setIsProcessing(true);
      try {
        await deactivateAccount(id);
        onAccountsChanged?.();
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleReactivate = async (id: string) => {
    setIsProcessing(true);
    try {
      await reactivateAccount(id);
      onAccountsChanged?.();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    const count = getTransactionCount(id);
    if (count > 0) {
      window.alert(`Cannot delete "${name}" because it has ${count} transaction${count !== 1 ? 's' : ''}. Deactivate it instead.`);
      return;
    }

    if (window.confirm(`Permanently delete account "${name}"?`)) {
      setIsProcessing(true);
      try {
        await deleteAccount(id);
        onAccountsChanged?.();
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newInstitution.trim()) return;

    setIsProcessing(true);
    try {
      const dto: CreateAccountDTO = {
        name: newName.trim(),
        type: newType,
        institution: newInstitution.trim(),
        lastFour: newLastFour.trim() || null,
        color: newColor,
        invertAmounts: newInvertAmounts,
      };
      await createAccount(dto);
      setNewName('');
      setNewType('checking');
      setNewInstitution('');
      setNewLastFour('');
      setNewColor('#4A90D9');
      setNewInvertAmounts(false);
      setIsCreating(false);
      onAccountsChanged?.();
    } finally {
      setIsProcessing(false);
    }
  };

  const renderAccountRow = (account: Account) => {
    const isEditing = editingId === account.id;
    const count = getTransactionCount(account.id);

    if (isEditing) {
      return (
        <div key={account.id} className={styles.accountRow}>
          <div className={styles.editForm}>
            <div className={styles.editRow}>
              <input
                type="text"
                className={styles.editInput}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Account name"
                autoFocus
              />
              <select
                className={styles.typeSelect}
                value={editType}
                onChange={(e) => setEditType(e.target.value as AccountType)}
              >
                {ACCOUNT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.editRow}>
              <input
                type="text"
                className={styles.editInput}
                value={editInstitution}
                onChange={(e) => setEditInstitution(e.target.value)}
                placeholder="Institution (e.g., Chase)"
              />
              <input
                type="text"
                className={styles.lastFourInput}
                value={editLastFour}
                onChange={(e) => setEditLastFour(e.target.value.slice(0, 4))}
                placeholder="Last 4"
                maxLength={4}
              />
            </div>
            <div className={styles.editRow}>
              <div className={styles.colorPicker}>
                <span
                  className={styles.colorPreview}
                  style={{ backgroundColor: editColor }}
                />
                <select
                  className={styles.colorSelect}
                  value={editColor}
                  onChange={(e) => setEditColor(e.target.value)}
                >
                  {PRESET_COLORS.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
              </div>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={editInvertAmounts}
                  onChange={(e) => setEditInvertAmounts(e.target.checked)}
                />
                Invert amounts (for credit cards)
              </label>
            </div>
            <div className={styles.rowActions}>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleEditCancel}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleEditSave}
                disabled={isProcessing || !editName.trim()}
                loading={isProcessing}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={account.id} className={`${styles.accountRow} ${!account.isActive ? styles.inactive : ''}`}>
        <span
          className={styles.colorIndicator}
          style={{ backgroundColor: account.color }}
        />
        <div className={styles.accountInfo}>
          <span className={styles.accountName}>{account.name}</span>
          <span className={styles.accountMeta}>
            {account.institution}
            {account.lastFour && ` •••• ${account.lastFour}`}
            {' · '}
            {getAccountTypeLabel(account.type)}
            {account.invertAmounts && ' · Inverted'}
          </span>
        </div>
        <span className={styles.transactionCount}>
          {count} txn{count !== 1 ? 's' : ''}
        </span>
        <div className={styles.rowActions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditStart(account)}
            disabled={isProcessing}
            aria-label={`Edit ${account.name}`}
          >
            Edit
          </Button>
          {account.isActive ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeactivate(account.id, account.name)}
              disabled={isProcessing}
              aria-label={`Deactivate ${account.name}`}
            >
              Deactivate
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleReactivate(account.id)}
                disabled={isProcessing}
                aria-label={`Reactivate ${account.name}`}
              >
                Reactivate
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(account.id, account.name)}
                disabled={isProcessing || count > 0}
                aria-label={`Delete ${account.name}`}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Manage Accounts</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </Button>
        </div>

        <div className={styles.content}>
          {/* Active Accounts */}
          <div className={styles.section}>
            <h3>Active Accounts</h3>
            <div className={styles.accountList}>
              {activeAccounts.length === 0 ? (
                <p className={styles.emptyMessage}>No accounts yet. Add one below.</p>
              ) : (
                activeAccounts.map(renderAccountRow)
              )}
            </div>
          </div>

          {/* Inactive Accounts */}
          {inactiveAccounts.length > 0 && (
            <div className={styles.section}>
              <h3>Inactive Accounts</h3>
              <div className={styles.accountList}>
                {inactiveAccounts.map(renderAccountRow)}
              </div>
            </div>
          )}

          {/* Add New Account */}
          {isCreating ? (
            <div className={styles.createForm}>
              <div className={styles.createRow}>
                <input
                  type="text"
                  className={styles.editInput}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Account name (e.g., Chase Sapphire)"
                  autoFocus
                />
                <select
                  className={styles.typeSelect}
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as AccountType)}
                >
                  {ACCOUNT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.createRow}>
                <input
                  type="text"
                  className={styles.editInput}
                  value={newInstitution}
                  onChange={(e) => setNewInstitution(e.target.value)}
                  placeholder="Institution (e.g., Chase)"
                />
                <input
                  type="text"
                  className={styles.lastFourInput}
                  value={newLastFour}
                  onChange={(e) => setNewLastFour(e.target.value.slice(0, 4))}
                  placeholder="Last 4"
                  maxLength={4}
                />
              </div>
              <div className={styles.createRow}>
                <div className={styles.colorPicker}>
                  <span
                    className={styles.colorPreview}
                    style={{ backgroundColor: newColor }}
                  />
                  <select
                    className={styles.colorSelect}
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                  >
                    {PRESET_COLORS.map((color) => (
                      <option key={color} value={color}>
                        {color}
                      </option>
                    ))}
                  </select>
                </div>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={newInvertAmounts}
                    onChange={(e) => setNewInvertAmounts(e.target.checked)}
                  />
                  Invert amounts (for credit cards)
                </label>
              </div>
              <div className={styles.createActions}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsCreating(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCreate}
                  disabled={isProcessing || !newName.trim() || !newInstitution.trim()}
                  loading={isProcessing}
                >
                  Add Account
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCreating(true)}
              fullWidth
              leftIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              }
            >
              Add New Account
            </Button>
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountManager;
